import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/ui/dialog';
import { AlertTriangle, ChevronDown, ChevronRight, MapPinIcon, TriangleAlert, X } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { cellIdToH3Index, H3Map, H3MapPolygonData } from '@/shared/components/H3Map';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { getServiceLineStatusBGColorClass, SERVICE_LINE_STATUS_VALUE } from '@/shared/utils/color-utils-service-lines';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';
import { MetricType, formatMetricValue } from '@/shared/utils/format-utils';
import SearchBar from '@/shared/components/SearchBar';
import EmptyView from '@/shared/components/EmptyView';

interface LocationMapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onManageServiceLine: (serviceLine: ServiceLine) => void;
  serviceLines: ServiceLine[];
}

export const LocationMapDialog = ({
  open,
  onOpenChange,
  onManageServiceLine,
  serviceLines,
}: LocationMapDialogProps) => {

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedAlertMessages, setSelectedAlertMessages] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedServiceLineNumber, setSelectedServiceLineNumber] = useState<string>('all');

  useEffect(() => {
    if (open) {
      clearFilters();
    }
  }, [open]);

  const clearFilters = () => {
    setStatusFilter('all');
    setSelectedAlertMessages([]);
    setSearchQuery('');
    setSelectedServiceLineNumber('all');
  };

  const serviceLineStatuses = useMemo(() => {
    return Array.from(new Set(serviceLines.map(sl => sl.status)));
  }, [serviceLines]);

  const filteredServiceLines = useMemo(() => {
    if (!serviceLines) return [];

    return serviceLines.filter(sl => {
      const statusText = SERVICE_LINE_STATUS_VALUE[sl.status];

      // Status filter
      if (statusFilter !== 'all' && statusText !== statusFilter) return false;

      // alert message filter
      if (selectedAlertMessages.length > 0) {
        const serviceLineAlertMessages = new Set(
          sl.terminals
            ?.flatMap((terminal) => terminal.activeAlerts || [])
            ?.map((alert) => alert?.message?.trim())
            ?.filter((message): message is string => Boolean(message)) ?? []
        );
        if (!selectedAlertMessages.some((message) => serviceLineAlertMessages.has(message))) return false;
      }

      // Search filter
      if (searchQuery && !sl.serviceLineName?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !sl.serviceLineNumber?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [serviceLines, statusFilter, searchQuery, selectedAlertMessages]);

  const alertMessageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    filteredServiceLines.forEach((serviceLine) => {
      serviceLine.terminals
        ?.flatMap((terminal) => terminal.activeAlerts || [])
        ?.forEach((alert) => {
          const message = alert?.message?.trim();
          if (!message) return;
          counts.set(message, (counts.get(message) ?? 0) + 1);
        });
    });
    return counts;
  }, [filteredServiceLines]);

  const toggleAlertSelection = (alertMessage: string) => {
    setSelectedAlertMessages((prev) =>
      prev.includes(alertMessage)
        ? prev.filter((item) => item !== alertMessage)
        : [...prev, alertMessage]
    );
  };

  const mapPolygonData: H3MapPolygonData[] = useMemo(() => {
    return filteredServiceLines.flatMap((serviceLine) => {
      if (!serviceLine.serviceLineNumber) return [];
      return serviceLine.terminals?.flatMap((terminal) => {
        const lastLocation = terminal.lastLocation;
        if (!lastLocation || lastLocation === '0') return [];
        try {
          // Keep normalized id for a stable selected/highlight state.
          return [{
            h3CellId: cellIdToH3Index(lastLocation),
            serviceLine,
          }];
        } catch {
          return [];
        }
      }) ?? [];
    });
  }, [filteredServiceLines]);

  const selectedServiceLineTerminals = useMemo(() => {
    return filteredServiceLines.find((serviceLine) => String(serviceLine.serviceLineNumber) === selectedServiceLineNumber)?.terminals;
  }, [filteredServiceLines, selectedServiceLineNumber]);

  const selectedServiceLine = useMemo(() => {
    return serviceLines.find((serviceLine) => String(serviceLine.serviceLineNumber) === selectedServiceLineNumber) ?? null;
  }, [serviceLines, selectedServiceLineNumber]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-4 border-b">
          <DialogTitle className="text-xl font-semibold">Service Lines' Location Map</DialogTitle>
          <DialogDescription>
            Service lines' location map showing online, offline, and active alerts status.
          </DialogDescription>
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4 justify-between mt-4">
              {/* Search */}
              <div className="relative w-full max-w-1/2">
                <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search service lines by name or number..." />
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {serviceLineStatuses.map((status) => {
                      const statusText = SERVICE_LINE_STATUS_VALUE[status];
                      if (statusText) {
                        return (
                          <SelectItem key={statusText} value={statusText}>
                            <Badge className={getServiceLineStatusBGColorClass(status)}>{statusText}</Badge>
                          </SelectItem>
                        );
                      }
                      return null;
                    })}
                  </SelectContent>
                </Select>

                {alertMessageCounts.size > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="min-w-28 gap-2 justify-start">
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <TriangleAlert className="w-4 h-4 text-amber-500" />
                            <span>{Array.from(alertMessageCounts.entries()).reduce((acc, [, count]) => acc + count, 0)}</span>
                          </div>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-72">
                      {Array.from(alertMessageCounts.entries()).length > 0 ? (
                        Array.from(alertMessageCounts.entries())
                          .sort((a, b) => b[1] - a[1])
                          .map(([alertMessage, count]) => (
                            <div key={alertMessage} className="flex items-center gap-2 px-2 py-1.5">
                              <Checkbox
                                checked={selectedAlertMessages.includes(alertMessage)}
                                onCheckedChange={() => toggleAlertSelection(alertMessage)}
                              />
                              <span className="text-sm font-medium">
                                {count} {alertMessage.split('_').join(' ').toUpperCase()}
                              </span>
                            </div>
                          ))
                      ) : (
                        <div className="px-2 py-2 text-sm">No alerts found</div>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <Button variant="outline" className="gap-2" onClick={() => {
                  clearFilters();
                }}>
                  <X className="h-4 w-4" />
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 flex-1 min-h-0 overflow-hidden border-t border-border">
          <div className="lg:col-span-1 bg-background min-h-0 overflow-y-auto border-r border-border">
            <div className="divide-y">
              {filteredServiceLines.map((serviceLine) => {
                const alertsCount = serviceLine.terminals?.flatMap((terminal) => terminal.activeAlerts || [])?.filter((alert) => alert?.message)?.length ?? 0;
                const statusText = SERVICE_LINE_STATUS_VALUE[serviceLine.status];
                return (
                  <div
                    key={serviceLine.serviceLineNumber}
                    className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${selectedServiceLineNumber === String(serviceLine.serviceLineNumber) ? ' bg-blue-500 text-white' : 'hover:bg-muted/40'
                      } ${!serviceLine.serviceLineNumber || !serviceLine.terminals || serviceLine.terminals.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => {
                      if (serviceLine.serviceLineNumber && serviceLine.terminals && serviceLine.terminals.length > 0) {
                        setSelectedServiceLineNumber(String(serviceLine.serviceLineNumber));
                      }
                    }}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{serviceLine.serviceLineName || '-'}</p>
                      <p className="text-xs truncate">{serviceLine.serviceLineNumber || '-'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {alertsCount > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1">
                                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                                  <span className="text-sm font-medium">{alertsCount}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs uppercase bg-black text-white">
                                <ul>{serviceLine.terminals?.flatMap((terminal) => terminal.activeAlerts || [])?.filter((alert) => alert?.message).map((alert, index) => <li key={index}>{alert?.message?.split('_')?.join(' ')}</li>)}</ul>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}
                      <Badge className={getServiceLineStatusBGColorClass(serviceLine.status)}>{statusText}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-2 min-h-0 h-full bg-muted/20">
            {mapPolygonData.length > 0 ? (
              <H3Map
                polygons={mapPolygonData}
                height="100%"
                fitOnResize
                selectedPolygon={selectedServiceLineNumber !== 'all' ? mapPolygonData.find((polygon) => String(polygon.serviceLine?.serviceLineNumber) === selectedServiceLineNumber) || null : null}
                onPolygonSelect={(polygonData) => {
                  setSelectedServiceLineNumber(String(polygonData.serviceLine?.serviceLineNumber ?? 'all'));
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <EmptyView
                  title="No location data available"
                  description="No location data available for the selected service lines."
                  size="large"
                  icon={MapPinIcon}
                  className="h-full w-full flex items-center justify-center"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {selectedServiceLineTerminals && selectedServiceLineTerminals.length > 0 && (
          <DialogFooter className="border-t w-full bg-muted/20 p-4">
            <div className="w-full rounded-lg bg-muted/50 shadow-sm space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  {selectedServiceLine && (
                    <>
                      <h3 className="text-sm font-semibold">
                        {selectedServiceLine?.serviceLineName || 'N/A'}
                      </h3>
                      <p className="text-xs">{selectedServiceLine?.serviceLineNumber || 'N/A'}</p>
                    </>
                  )}
                </div>
                <Button
                  variant="default"
                  className="gap-2 shrink-0 justify-end"
                  onClick={() => {
                    if (selectedServiceLine) {
                      onManageServiceLine(selectedServiceLine);
                    }
                    onOpenChange(false);
                  }}
                >
                  View Service Line Details
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                {selectedServiceLineTerminals.map((terminal) => (
                  <div key={terminal.userTerminalId} className="flex flex-row gap-4 items-center justify-between bg-background p-4 rounded-lg w-full shadow-sm">
                    <div className="flex flex-col items-start gap-1 min-w-0">
                      <p className="text-sm font-semibold">Terminal Information</p>
                      <div className="flex flex-col gap-1 text-sm">
                        {terminal.kitSerialNumber && <p>Kit Serial Number: <span className="font-medium">{terminal.kitSerialNumber}</span></p>}
                        <div className="flex flex-row gap-2 items-center justify-between">
                          {terminal.userTerminalId && <p>ID: <span className="font-medium">{terminal.userTerminalId}</span></p>}
                          {terminal.dishSerialNumber && <p>Dish Serial Number: <span className="font-medium">{terminal.dishSerialNumber}</span></p>}
                        </div>
                        {terminal.type && <p>Type: <span className="font-medium">{terminal.type.split('_').join(' ').toUpperCase()}</span></p>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <p className="text-sm font-semibold">Active Alerts</p>
                      {terminal.activeAlerts?.length > 0 ? terminal.activeAlerts?.map((alert) => (
                        <p className="text-sm font-medium" key={alert.code}>{alert.message?.split('_').join(' ').toUpperCase()}</p>
                      )) : <p className="text-sm font-medium">None</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
